import { inject, Injectable } from '@angular/core';
import { APP_API_ENDPOINTS } from '../constants/app-api.const';
import { APP_ROUTES } from '../constants/app-routes.const';
import { PermissionNode } from '../models/permission.model';
import { User } from '../models/user.model';
import { AuthStore } from '../stores/auth.store';
import { ApiService } from './api.service';

@Injectable({
  providedIn: 'root',
})
export class PermissionService {
  private readonly apiService = inject(ApiService);
  private readonly store = inject(AuthStore);

  /**
   * Tải thông tin User và Cây Menu (Permissions)
   */
  async loadUserAndPermissions(): Promise<void> {
    let res;

    try {
      res = await this.apiService.toPromise(
        this.apiService.get<User>(APP_API_ENDPOINTS.USER.INFO),
      );
    } catch (error) {
      // Xóa session cũ để tránh vòng lặp guard khi /users/me lỗi
      // (ví dụ endpoint sai, token stale, backend tạm chết).
      this.store.clearAll();
      throw error;
    }

    if (res.data) {
      const user = res.data;
      this.store.setUser(user);
      if (user.menu) {
        this.store.setMenuTree(user.menu);
      }
    }
  }

  /**
   * Truy tìm Route (Lá) đầu tiên mà User có quyền truy cập.
   * Ưu tiên duyệt sâu để tìm item nhỏ nhất có path (Logic B).
   */
  getFirstAccessibleRoute(
    menu: PermissionNode[] = this.store.menuTree(),
  ): string {
    const forbiddenPath = '/' + APP_ROUTES.FORBIDDEN;
    if (!menu || menu.length === 0) return forbiddenPath;

    for (const item of menu) {
      // 1. Duyệt sâu vào con trước để tìm "Lá" thực sự
      if (item.children && item.children.length > 0) {
        const childRoute = this.getFirstAccessibleRoute(item.children);
        if (childRoute !== forbiddenPath) return childRoute;
      }

      // 2. Nếu là Lá (hoặc item có path) và có quyền đọc -> Trả về path
      if (item.is_read > 0 && item.path) {
        return item.path;
      }
    }

    return forbiddenPath;
  }

  /**
   * Kiểm tra User có quyển truy cập vào Menu/Screen cụ thể không (Dựa trên ID)
   */
  hasMenuAccess(permissionId: string): boolean {
    const permission = this.store.permissionMap().get(permissionId);
    return !!permission && permission.is_read > 0;
  }

  /**
   * Dọn dẹp phân quyền khi logout
   */
  clear() {
    this.store.clearAll();
  }
}
